import { motion } from 'framer-motion';
import type { Testimonial } from '../../data/testimonials';

interface TestimonialCardProps {
  testimonial: Testimonial;
  index?: number;
  variant?: 'default' | 'featured' | 'compact' | 'quote-only';
}

export default function TestimonialCard({ testimonial, index = 0, variant = 'default' }: TestimonialCardProps) {
  const { name, location, age, quote, shortQuote } = testimonial;

  // Generate initials for avatar placeholder
  const initials = name.split(' ').map(n => n[0]).join('');

  if (variant === 'quote-only') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1 }}
        style={{
          padding: '1.5rem',
          background: 'var(--light-gray)',
          borderRadius: '1rem'
        }}
      >
        <p style={{ fontStyle: 'italic', marginBottom: '1rem', fontSize: 'clamp(0.9375rem, 2vw, 1rem)' }}>
          "{shortQuote || quote}"
        </p>
        <p style={{ fontWeight: 600, margin: 0, fontSize: 'clamp(0.875rem, 2vw, 0.9375rem)' }}>
          — {name}
        </p>
      </motion.div>
    );
  }

  if (variant === 'compact') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1 }}
        style={{
          padding: '1.25rem',
          background: 'white',
          borderRadius: '0.75rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}
      >
        <p style={{ fontStyle: 'italic', marginBottom: '0.75rem', fontSize: 'clamp(0.875rem, 2vw, 1rem)', color: 'var(--text-light)' }}>
          "{shortQuote || quote}"
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: 'var(--accent)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.875rem',
            fontWeight: 600
          }}>
            {initials}
          </div>
          <div>
            <p style={{ fontWeight: 600, margin: 0, fontSize: '0.9375rem' }}>{name}</p>
            <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--text-light)' }}>{location}</p>
          </div>
        </div>
      </motion.div>
    );
  }

  if (variant === 'featured') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1 }}
        style={{
          padding: '2rem',
          background: 'linear-gradient(135deg, var(--white) 0%, var(--light-gray) 100%)',
          borderRadius: '1rem',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          border: '2px solid var(--accent-light)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: 'var(--accent)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.25rem',
            fontWeight: 600,
            flexShrink: 0
          }}>
            {initials}
          </div>
          <div>
            <p style={{ fontWeight: 700, margin: 0, fontSize: '1.125rem' }}>{name}</p>
            <p style={{ margin: 0, fontSize: '0.9375rem', color: 'var(--text-light)' }}>
              {location}{age ? `, ${age}` : ''}
            </p>
          </div>
        </div>
        <p style={{
          fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
          lineHeight: 1.7,
          margin: 0,
          color: 'var(--text)'
        }}>
          "{quote}"
        </p>
      </motion.div>
    );
  }

  // Default variant
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      style={{
        padding: '1.5rem',
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', marginBottom: '1rem' }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: 'var(--accent)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1rem',
          fontWeight: 600,
          flexShrink: 0
        }}>
          {initials}
        </div>
        <div>
          <p style={{ fontWeight: 600, margin: 0, fontSize: '1rem' }}>{name}</p>
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-light)' }}>
            {location}{age ? `, ${age}` : ''}
          </p>
        </div>
      </div>
      <p style={{
        fontSize: 'clamp(0.9375rem, 2vw, 1rem)',
        lineHeight: 1.6,
        margin: 0,
        color: 'var(--text-light)',
        fontStyle: 'italic'
      }}>
        "{shortQuote || quote}"
      </p>
    </motion.div>
  );
}

// Grid component for multiple testimonials
interface TestimonialGridProps {
  testimonials: Testimonial[];
  columns?: 2 | 3 | 4;
  variant?: 'default' | 'featured' | 'compact' | 'quote-only';
}

export function TestimonialGrid({ testimonials, columns = 3, variant = 'default' }: TestimonialGridProps) {
  const gridClass = columns === 2 ? 'grid-2' : columns === 4 ? 'grid-auto' : 'grid-3';

  return (
    <div className={gridClass} style={{ gap: '1.5rem' }}>
      {testimonials.map((testimonial, index) => (
        <TestimonialCard
          key={testimonial.id}
          testimonial={testimonial}
          index={index}
          variant={variant}
        />
      ))}
    </div>
  );
}
